/* BadgeMovements.tsx
    Une page affichant la gestion des mouvements de badges
    Utilise le composant GenericCrudPage pour lister, ajouter, gérer et supprimer les mouvements de badges
    Utilise l'API correspondante pour les opérations de données
*/
import React, { useState } from 'react';

import { BadgeMovementsCrudCard } from 'features/badgeMovement/components/BadgeMovementsCrudCard';
import { externalConnectionsApi } from 'features/externalConnection/services/externalConnectionsApi';
import {
	faArrowRightArrowLeft,
	faDownload,
	faFileImport,
	faPlugCircleCheck,
	faTable,
} from '@fortawesome/free-solid-svg-icons';
import { MainButton, SecondaryButton } from 'common';
import PageHeader from 'layout/PageHeader';
import WidgetSection from 'layout/WidgetSection';
import WidgetHeader from 'layout/WidgetHeader';

// Badge movements page component
export const BadgeMovements: React.FC = () => {
	// State management
	type ImportParams = {
		fromOriginalDateTime?: string;
		limit?: number;
		dryRun?: boolean;
	};
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<unknown | null>(null);
	const [importParams, setImportParams] = useState<ImportParams>({
		dryRun: false,
	});

	const handleTestConnection = async () => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		const connectionId = 1; // example connection ID
		setLoading(true);
		try {
			const res = await externalConnectionsApi.testConnection(
				connectionId,
				token
			);
			setResult(res);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = async () => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		const connectionId = 1; // example connection ID
		setLoading(true);
		try {
			const res = await externalConnectionsApi.previewMovements(
				connectionId,
				token
			);
			setResult(res);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const handleImport = async () => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		const connectionId = 1; // example connection ID
		setLoading(true);
		try {
			const res = await externalConnectionsApi.importMovements(
				connectionId,
				token,
				importParams
			);
			setResult(res);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	// Render the badge movements page
	return (
		<div className="min-h-screen bg-gray-100 space-y-8 p-6">
			{/* Titre de page */}
			<PageHeader
				icon={faArrowRightArrowLeft}
				title="Mouvements de badges"
				subtitle="Entrées et sorties des personnels grâce à leurs badges"
				className="!max-w-full"
			/>

			<BadgeMovementsCrudCard size="lg" hideReturn className="!max-w-full" />

			<WidgetSection className="!max-w-full">
				<WidgetHeader icon={faFileImport} title="Importation" />

				{/* Paramètres d'import */}
				<div className="flex flex-wrap items-end gap-4 mb-6">
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-700">
							Depuis le
						</label>
						<input
							type="datetime-local"
							value={importParams.fromOriginalDateTime || '2026-01-01T00:00'}
							onChange={(e) =>
								setImportParams({
									...importParams,
									fromOriginalDateTime: e.target.value,
								})
							}
							className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className="text-sm font-medium text-gray-700">Limite</label>
						<input
							type="number"
							value={importParams.limit || ''}
							onChange={(e) =>
								setImportParams({
									...importParams,
									limit: parseInt(e.target.value) || undefined,
								})
							}
							placeholder="Illimitée"
							className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 w-36"
						/>
					</div>
					<div className="flex items-center gap-2 pb-1.5">
						<input
							type="checkbox"
							id="dryRun"
							checked={importParams.dryRun || false}
							onChange={(e) =>
								setImportParams({ ...importParams, dryRun: e.target.checked })
							}
							className="w-4 h-4 accent-indigo-600 cursor-pointer"
						/>
						<label
							htmlFor="dryRun"
							className="text-sm font-medium text-gray-700 cursor-pointer select-none"
						>
							Dry run (simulation sans import)
						</label>
					</div>
				</div>

				{/* Boutons — tous alignés à droite, primary le plus à droite */}
				<div className="flex flex-wrap items-center justify-end gap-3 mb-6">
					<SecondaryButton
						label="Tester la connexion"
						onClick={handleTestConnection}
						icon={faPlugCircleCheck}
						disabled={loading}
					/>
					<SecondaryButton
						label="Prévisualiser la table"
						onClick={handlePreview}
						icon={faTable}
						disabled={loading}
					/>
					<MainButton
						label="Importer maintenant"
						onClick={handleImport}
						icon={faDownload}
						disabled={loading}
					/>
				</div>

				{/* Résultat brut */}
				{result != null && (
					<pre className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 overflow-x-auto">
						{JSON.stringify(result, null, 2)}
					</pre>
				)}
			</WidgetSection>
		</div>
	);
};

export default BadgeMovements;
